import { isNode, isEdge } from "react-flow-renderer";

export const flowToDatabaseTrail = elements => {
  const trailActivities = [];
  const edges = [];

  elements.forEach(element => {
    if (isNode(element)) {
      trailActivities.push({
        id: element.id.replace("node-", ""),
        activity_id: element.data?.activity?.id,
        x: element.position.x,
        y: element.position.y,
        type: element.type,
        temporary: element.data?.temporary ?? true,
        start: element.data?.start,
        end: element.data?.end
      });
    } else {
      edges.push({
        id: element.id.replace("edge-", ""),
        source_id: element.source.toString().replace("node-", ""),
        target_id: element.target.toString().replace("node-", ""),
        type: "default",
        temporary: element.data?.temporary ?? true,
        first: element.data?.first,
        operator: element.data?.operator,
        second: element.data?.second
      });
    }
  });

  return {
    trailActivities,
    edges
  };
};

export const databaseToFlowTrail = (trailActivities, edges) => {
  const flowNodes = trailActivities.map(trailActivity => ({
    id: `node-${trailActivity.id.toString()}`,
    type: trailActivity.type,
    position: {
      x: Number(trailActivity.x),
      y: Number(trailActivity.y)
    },
    data: {
      temporary: false,
      activity: trailActivity.activity,
      start: trailActivity.start,
      end: trailActivity.end
    }
  }));

  const flowEdges = edges.map(edge => ({
    id: `edge-${edge.id.toString()}`,
    source: `node-${edge.source_id.toString()}`,
    target: `node-${edge.target_id.toString()}`,
    type: "default",
    data: {
      temporary: false,
      first: edge.first,
      operator: edge.operator,
      second: edge.second
    }
  }));

  return [...flowNodes, ...flowEdges];
};

export const styleTrail = (
  elements,
  addElement = () => null,
  editElement = () => null,
  deleteElement = () => null,
  setDrag = () => null,
  editable = false
) => {
  const nodes = elements.filter(isNode);

  nodes.forEach(node => {
    node.data = {
      ...node.data,
      activeInput: false,
      activeOutput: false,
      addElement,
      editElement,
      deleteElement,
      setDrag,
      editable,
      doing: false,
      onClick: () => null,
      active: false
    };
  });

  const edges = elements.filter(isEdge);

  edges.forEach(edge => {
    edge.animated = false;

    edge.arrowHeadType = "arrowclosed";

    edge.style = {
      stroke: editable ? "var(--primary)" : "var(--secondary)",
      strokeWidth: 2
    };

    edge.data = {
      ...edge.data,
      editElement,
      setDrag,
      editable,
      doing: false,
      active: false
    };
  });

  return [...nodes, ...edges];
};

export const styleTrailExecution = (elements, onClick = () => null) => {
  const markedElements = setActiveElements(elements);

  const nodes = markedElements.filter(isNode);

  nodes.forEach(node => {
    node.data = {
      ...node.data,
      activeInput: false,
      activeOutput: false,
      addElement: () => null,
      editElement: () => null,
      setDrag: () => null,
      deleteElement: () => null,
      editable: false,
      doing: true,
      onClick
    };
  });

  const edges = markedElements.filter(isEdge);

  edges.forEach(edge => {
    edge.animated = edge.data?.active;

    edge.arrowHeadType = "arrowclosed";

    edge.style = {
      stroke: edge.data?.active ? "var(--primary)" : "var(--secondary)",
      strokeWidth: 2
    };

    edge.data = {
      ...edge.data,
      editElement: () => null,
      setDrag: () => null,
      editable: false,
      doing: true
    };
  });

  return [...nodes, ...edges];
};

export const getTodoElements = elements => {
  const nodes = elements.filter(isNode).map(node => ({
    ...node,
    data: { ...node.data, active: false, toDo: false }
  }));
  const edges = elements.filter(isEdge).map(edge => ({
    ...edge,
    data: { ...edge.data, possible: false, active: false }
  }));

  const startNode = nodes.find(node => node.type === "start");

  const markNode = node => {
    node.data.active = true;

    const nodeExecution =
      node.data?.activity?.executions.length > 0
        ? node.data.activity.executions[
            node.data.activity.executions.length - 1
          ]
        : null;
    const currentEdges = edges.filter(edge => edge.source === node.id);

    if (
      node.data.active &&
      (!nodeExecution || nodeExecution.completion_percentage < 100)
    ) {
      node.data.toDo = true;
    }

    currentEdges.forEach(edge => {
      edge.data.possible = true;

      if (
        checkCondition(
          nodeExecution,
          edge,
          node.data?.activity?.items?.length,
          node.data?.start,
          node.data?.end
        )
      ) {
        edge.data.active = true;

        const targetNode = nodes.find(element => element.id === edge.target);

        markNode(targetNode);
      }
    });
  };

  markNode(startNode);

  return [...nodes.filter(node => node.data.toDo)];
};

const setActiveElements = elements => {
  const nodes = elements
    .filter(isNode)
    .map(node => ({ ...node, data: { ...node.data, active: false } }));
  const edges = elements.filter(isEdge).map(edge => ({
    ...edge,
    data: { ...edge.data, possible: false, active: false }
  }));

  const startNode = nodes.find(node => node.type === "start");

  const markNode = node => {
    node.data.active = true;

    const nodeExecution =
      node.data?.activity?.executions.length > 0
        ? node.data.activity.executions[
            node.data.activity.executions.length - 1
          ]
        : null;
    const currentEdges = edges.filter(edge => edge.source === node.id);

    currentEdges.forEach(edge => {
      edge.data.possible = true;

      if (
        checkCondition(
          nodeExecution,
          edge,
          node.data?.activity?.items?.length,
          node.data?.start,
          node.data?.end
        )
      ) {
        edge.data.active = true;

        const targetNode = nodes.find(element => element.id === edge.target);

        markNode(targetNode);
      }
    });
  };

  markNode(startNode);

  return [...nodes, ...edges];
};

const checkCondition = (execution, edge, itemsCount = 1, start, end) => {
  if (!execution) {
    return false;
  }

  if (Date.parse(execution.start) < Date.parse(start)) {
    return false;
  }

  if (Date.parse(execution.start) > Date.parse(end)) {
    return false;
  }

  if (!edge.data.first) {
    return true;
  }

  if (edge.data.first === "grade" && execution.items.length >= itemsCount) {
    const itemWithGrade = execution.items.filter(
      item => item.final_grade !== null && item.final_grade !== ""
    );
    const grade =
      itemWithGrade.reduce((acc, item) => acc + Number(item.final_grade), 0) /
      itemWithGrade.length;

    return mathOperators[edge.data.operator](grade, Number(edge.data.second));
  }

  if (edge.data.first === "percentage") {
    const percentage =
      execution.items.length === 0
        ? execution.completion_percentage
        : execution.items.reduce(
            (acc, item) => acc + Number(item.completion_percentage),
            0
          ) / itemsCount;

    return mathOperators[edge.data.operator](
      percentage,
      Number(edge.data.second.replace("%", ""))
    );
  }

  return false;
};

const mathOperators = {
  ">": (x, y) => x > y,
  ">=": (x, y) => x >= y,
  "<": (x, y) => x < y,
  "<=": (x, y) => x <= y,
  "===": (x, y) => x === y
};
